/**
 * Human-friendly error translation.
 *
 * The service returns structured errors like:
 *   { error: { code: "BRIEF_DUPLICATE_IN_PROGRESS", type: "CONFLICT", message: "..." } }
 *
 * Users should never see raw codes, stack traces, database messages, or
 * validation-library envelopes. `humanizeApiError()` maps any thrown value
 * into a `{ title, description, hint? }` triple that reads like something a
 * human would write.
 *
 * The raw error is still logged to the browser console so developers can
 * inspect it in dev-tools; only the returned strings are safe to display.
 */
import { ApiError, extractFieldErrors, extractFormErrors } from './api';

export interface HumanError {
  /** Short label suitable for a toast title or banner heading. */
  title: string;
  /** One or two sentences: what happened + what to do next. */
  description: string;
  /** Optional secondary sentence with more context (kept short). */
  hint?: string;
}

/** Generic fallback used any time we cannot recognise the error shape. */
const GENERIC: HumanError = {
  title: 'Something went wrong',
  description: 'Something went wrong. Please try again.',
};

/**
 * Static (code → message) mapping. Order-insensitive; suffix rules below
 * catch anything not enumerated here (e.g. `WIDGET_NOT_FOUND` matches the
 * `_NOT_FOUND` suffix rule).
 */
const CODE_MAP: Record<string, HumanError> = {
  // Auth
  AUTH_MISSING: {
    title: 'Please sign in',
    description: 'Please sign in to continue.',
  },
  AUTH_INVALID: {
    title: 'Please sign in',
    description: 'Please sign in to continue.',
  },
  JWT_INVALID: {
    title: 'Session expired',
    description: 'Please sign in to continue.',
  },
  UNAUTHORIZED: {
    title: 'Please sign in',
    description: 'Please sign in to continue.',
  },

  // Permission
  FORBIDDEN: {
    title: "You don't have permission",
    description: "You don't have permission to do this.",
  },
  AUTH_TENANT_FORBIDDEN: {
    title: "You don't have permission",
    description: "You don't have permission to do this.",
  },

  // Not found
  NOT_FOUND: {
    title: 'Not found',
    description: "We couldn't find that.",
  },

  // Conflict / duplicate / raced
  CONFLICT: {
    title: 'Already exists',
    description: 'That already exists. Refresh to see the latest.',
  },
  IDEMPOTENCY_CONFLICT: {
    title: 'Duplicate request',
    description: "We've already handled a request just like this one.",
  },
  BRIEF_DUPLICATE_IN_PROGRESS: {
    title: 'Already in progress',
    description:
      'A brief for this keyword is already being generated. Please wait or refresh.',
  },

  // Budget / quota / rate
  BUDGET_EXCEEDED: {
    title: 'Plan limit reached',
    description:
      "You've hit your plan's limit. Upgrade or wait for the monthly reset.",
  },
  LLM_BUDGET_EXCEEDED: {
    title: 'AI usage limit reached',
    description: "You've hit your AI writer usage limit for this plan cycle.",
  },
  AUDIT_QUOTA_EXCEEDED: {
    title: 'Audit quota used',
    description: "You've used all the audits included in your plan.",
  },
  RATE_LIMITED: {
    title: 'Too many requests',
    description: 'Too many requests — please wait a moment and try again.',
  },

  // Server errors
  INTERNAL_ERROR: {
    title: 'Temporary hiccup',
    description: 'Something went wrong on our side. Try again — no cost incurred.',
  },
  SERVICE_UNAVAILABLE: {
    title: 'Temporarily unavailable',
    description:
      "We're temporarily unavailable. Try again in a moment — no cost incurred.",
  },

  // URL fetching / SSRF guards
  SSRF_BLOCKED: {
    title: 'That URL is not allowed',
    description: "That URL isn't allowed. Use a public web address.",
  },
  SSRF_INVALID_URL: {
    title: 'That URL is not allowed',
    description: "That URL isn't allowed. Use a public web address.",
  },
  SSRF_PRIVATE_ADDRESS: {
    title: 'That URL is not allowed',
    description: "That URL isn't allowed. Use a public web address.",
  },

  // Keyword universe
  KW_EXPAND_NO_SEEDS: {
    title: 'Not enough seeds',
    description:
      'Not enough info to build a keyword universe. Add more seed keywords.',
  },

  // Brief lifecycle
  BRIEF_REGEN_NO_CONTENT: {
    title: 'Brief not ready yet',
    description:
      "This brief hasn't finished its first draft yet. Wait for it to complete before regenerating.",
  },

  // Competitor analysis lifecycle
  CA_STRATEGY_NOT_READY: {
    title: 'Analysis still running',
    description: 'The competitor analysis is still running. Come back in a minute.',
  },
  CA_BAD_STATE: {
    title: 'Analysis not ready',
    description: "This analysis isn't in a state that supports that action yet.",
  },
  CA_NO_STRATEGY: {
    title: 'Analysis still running',
    description: 'The competitor analysis is still running. Come back in a minute.',
  },

  // Validation
  VALIDATION_FAILED: {
    title: 'Check the highlighted fields',
    description:
      'Some fields need attention — please review the highlighted inputs.',
  },
};

/**
 * Suffix-based rules for whole families of codes. Applied only when the
 * static map above did not produce a match.
 */
function fromSuffix(code: string): HumanError | undefined {
  if (code.endsWith('_DEGRADED')) {
    // Server-side already made these messages user-friendly; we let the
    // caller override the description with the message text.
    return {
      title: 'Partial result',
      description:
        'Some data was skipped because a step could not complete. Try again shortly.',
    };
  }
  if (code.endsWith('_NOT_FOUND')) {
    return {
      title: 'Not found',
      description: "We couldn't find that.",
    };
  }
  if (code.endsWith('_DUPLICATE') || code.endsWith('_ALREADY_EXISTS')) {
    return {
      title: 'Already exists',
      description: 'You already have one of these. Refresh to see the latest.',
    };
  }
  if (code.endsWith('_RACED')) {
    return {
      title: 'Already in progress',
      description: 'A similar job is already running. Wait for it to finish or refresh.',
    };
  }
  if (code.endsWith('_BUDGET_EXCEEDED') || code.includes('_BUDGET_')) {
    return {
      title: 'Plan limit reached',
      description:
        "You've hit your plan's limit. Upgrade or wait for the monthly reset.",
    };
  }
  if (code.endsWith('_QUOTA_EXCEEDED')) {
    return {
      title: 'Plan quota used',
      description:
        "You've used the allowance included in your plan. Upgrade or wait for the monthly reset.",
    };
  }
  if (code.startsWith('SSRF_')) {
    return {
      title: 'That URL is not allowed',
      description: "That URL isn't allowed. Use a public web address.",
    };
  }
  return undefined;
}

/** Convert an HTTP status into a HumanError when no code is available. */
function fromStatus(status: number | undefined): HumanError {
  if (status === undefined) return GENERIC;
  if (status === 401) return CODE_MAP.AUTH_INVALID!;
  if (status === 403) return CODE_MAP.FORBIDDEN!;
  if (status === 404) return CODE_MAP.NOT_FOUND!;
  if (status === 409) return CODE_MAP.CONFLICT!;
  if (status === 422) return CODE_MAP.VALIDATION_FAILED!;
  if (status === 429) return CODE_MAP.RATE_LIMITED!;
  if (status === 503) return CODE_MAP.SERVICE_UNAVAILABLE!;
  if (status >= 500) return CODE_MAP.INTERNAL_ERROR!;
  return GENERIC;
}

/**
 * Combine per-field validation messages into a single readable string.
 * Returns `undefined` if the error carries no structured field errors.
 */
function fieldErrorSummary(err: ApiError): string | undefined {
  const fieldErrors = extractFieldErrors(err);
  const formErrors = extractFormErrors(err);
  const lines: string[] = [];
  for (const [field, message] of Object.entries(fieldErrors)) {
    lines.push(`${prettyField(field)}: ${message}`);
  }
  for (const m of formErrors) lines.push(m);
  return lines.length > 0 ? lines.join('\n') : undefined;
}

function prettyField(field: string): string {
  const cleaned = field.replace(/_id$/i, '').replace(/_/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/**
 * Strip technical vocabulary from a server-provided message so it reads like
 * something a human would write. Applied only when we fall back to using the
 * server's own message text.
 */
function sanitizeMessage(raw: string): string {
  let s = raw;
  // Drop any leading "HTTP 500" / "500 Internal Server Error" prefix.
  s = s.replace(/^HTTP\s*\d{3}[:\s-]*/i, '');
  s = s.replace(/\b(HTTP\s*)?5\d\d\s+[A-Za-z ]+\b/g, '').trim();
  // Rewrite backend jargon.
  const jargon: Array<[RegExp, string]> = [
    [/\bworkflows\b/gi, 'jobs'],
    [/\bworkflow\b/gi, 'job'],
    [/\bactivities\b/gi, 'steps'],
    [/\bactivity\b/gi, 'step'],
    [/\btemporal\b/gi, 'job runner'],
    [/\bcompensation(s)?\b/gi, 'rollback$1'],
    [/\boutbox\b/gi, 'delivery queue'],
    [/\bFSM\b/g, 'state'],
    [/\btransition(ed|s)?\b/gi, 'change$1'],
    [/\bbudget gate\b/gi, 'plan limit'],
    [/\bSSRF\b/g, 'URL policy'],
    [/\bZod\b/g, 'the form'],
    [/\bDrizzle\b/g, 'the database'],
    [/\bpostgres(ql)?\b/gi, 'the database'],
    [/\bAPI\b/g, 'the service'],
  ];
  for (const [re, sub] of jargon) s = s.replace(re, sub);
  // Cap length so a runaway server message doesn't fill the toast.
  s = s.trim();
  if (s.length > 240) s = `${s.slice(0, 237)}…`;
  return s;
}

/**
 * Look up a human-readable HumanError for any thrown value.
 *
 * Order of precedence:
 *   1. Structured API errors — code map, then suffix rules, then HTTP status.
 *   2. Validation errors — merge field messages into the description.
 *   3. Generic Errors — sanitize and surface `error.message` as description.
 *   4. Anything else — the GENERIC fallback.
 *
 * The raw error is `console.debug`-logged so developers can still see it.
 */
export function humanizeApiError(err: unknown): HumanError {
  // Always log the raw error for developers; users see only the returned copy.
  try {
    // eslint-disable-next-line no-console
    console.debug('[humanizeApiError]', err);
  } catch {
    // ignore logging failures
  }

  if (err instanceof ApiError) {
    // Validation errors get special treatment — we surface the field messages.
    if (err.code === 'VALIDATION_FAILED' || err.status === 422) {
      const desc = fieldErrorSummary(err);
      if (desc) {
        return {
          title: 'Check the highlighted fields',
          description: desc,
        };
      }
      return CODE_MAP.VALIDATION_FAILED!;
    }

    // 1. Exact code match.
    const exact = CODE_MAP[err.code];
    if (exact) return exact;

    // 2. Suffix / family rules.
    const suffix = fromSuffix(err.code);
    if (suffix) {
      // For `_DEGRADED` responses the server has already crafted a friendly
      // message; honour it when present.
      if (err.code.endsWith('_DEGRADED') && err.message) {
        return { ...suffix, description: sanitizeMessage(err.message) };
      }
      return suffix;
    }

    // 3. HTTP status fallback.
    const byStatus = fromStatus(err.status);
    if (byStatus !== GENERIC) return byStatus;

    // 4. Server message as last resort — sanitised.
    if (err.message && err.message !== 'Request validation failed') {
      return {
        title: GENERIC.title,
        description: sanitizeMessage(err.message),
      };
    }
    return GENERIC;
  }

  // Non-ApiError values (network failures, TypeErrors, etc.).
  if (err instanceof Error && err.message) {
    return {
      title: GENERIC.title,
      description: sanitizeMessage(err.message),
    };
  }
  return GENERIC;
}
